import React, {FC} from 'react';
import {styled, useTheme} from 'styled-components';

import {ChannelExternalTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {
  ChannelIcon,
  ChannelIconSizesEnum,
  ChannelIconVisualTypesEnum,
} from '../../../../../libs/shared/ui/src/components/icon/channelIcon';
import ChannelArrow from '../../../../assets/images/basicsGuide/channelsArrow.svg';
import MessageFlourish from '../../../../assets/images/basicsGuide/messageFlourish.svg';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getFirstTeamUserIsAdminOf} from '../../../../store/data/teams/teamsSelectors';
import {onboardingGuideRightSideWidth, onboardingGuideSlideHeight} from '../../guide/onboardingGuideHelpers';
import {
  OnboardingInboxIllustration,
  OnboardingInboxVisualFeature,
} from '../../welcome/workflows/onboardingInboxIllustration';
import {StyledVisualContainer} from './onboardingBasicsGuideHelpers';

/*
 * Props.
 */

interface OnboardingBasicsGuideImportMessagesVisualProps {}

/*
 * Style.
 */

const StyledImportMessagesVisualContainer = styled.div`
  position: absolute;
  width: ${onboardingGuideRightSideWidth};
  height: ${onboardingGuideSlideHeight};
`;

const StyledChannelIcon = styled(ChannelIcon)`
  position: absolute;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
  z-index: 1;
`;

const StyledOnboardingInboxIllustration = styled(OnboardingInboxIllustration)`
  position: absolute;
  top: 105px;
  left: 142px;

  ${computeFadeInAnimation('inboxIllustrationFadeIn', 400, 800)}
`;

const StyledMicrosoftChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(319, 32)}px;
  left: ${computeOffset(58, 32)}px;
  transform: scale(${32 / 40}) rotate(-10.63deg);

  ${computeFadeInAnimation('microsoftChannelIconFadeIn', 1000, 400, 32 / 40)}
`;

const StyledTwitterChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(355, 22)}px;
  left: ${computeOffset(55, 22)}px;
  transform: scale(${22 / 40}) rotate(19.14deg);

  ${computeFadeInAnimation('twitterChannelIconFadeIn', 1200, 400, 22 / 40)}
`;

const StyledWhatsappChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(343, 32)}px;
  left: ${computeOffset(86, 32)}px;
  transform: scale(${32 / 40}) rotate(37.24deg);

  ${computeFadeInAnimation('whatsappChannelIconFadeIn', 1400, 400, 32 / 40)}
`;

const StyledInstagramChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(376, 27)}px;
  left: ${computeOffset(71, 27)}px;
  transform: scale(${27 / 40}) rotate(25.8deg);

  ${computeFadeInAnimation('instagramChannelIconFadeIn', 1600, 400, 27 / 40)}
`;

const StyledGmailChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(393, 32)}px;
  left: ${computeOffset(39, 32)}px;
  transform: scale(${32 / 40}) rotate(11.33deg);

  ${computeFadeInAnimation('gmailChannelIconFadeIn', 1800, 400, 32 / 40)}
`;

const StyledFacebookChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(410, 26)}px;
  left: ${computeOffset(81, 26)}px;
  transform: scale(${26 / 40}) rotate(19.14deg);

  ${computeFadeInAnimation('facebookChannelIconFadeIn', 2000, 400, 26 / 40)}
`;

const StyledChannelArrow = styled.img`
  position: absolute;
  top: 386px;
  left: 116px;
  z-index: 1;

  ${computeFadeInAnimation('channelArrowFadeIn', 2800, 600)}
`;

const StyledMessageFlourish = styled.img`
  position: absolute;
  top: 147px;
  left: 346px;
  z-index: 1;

  ${computeFadeInAnimation('messageFlourishFadeIn', 4800, 600)}
`;

/*
 * Component.
 */

export const OnboardingBasicsGuideImportMessagesVisual: FC<
  OnboardingBasicsGuideImportMessagesVisualProps
> = () => {
  const theme = useTheme();
  const defaultTeam = useAppSelector(getFirstTeamUserIsAdminOf);
  return (
    <StyledVisualContainer>
      <StyledImportMessagesVisualContainer>
        <StyledMessageFlourish src={MessageFlourish} alt="Import messages into Front!" />
        <StyledChannelArrow src={ChannelArrow} alt="Import messages into Front!" />

        <StyledMicrosoftChannelIcon
          type={ChannelIconVisualTypesEnum.MICROSOFT}
          size={ChannelIconSizesEnum.BIG}
        />
        <StyledTwitterChannelIcon type={ChannelTypesEnum.TWITTER} size={ChannelIconSizesEnum.BIG} />
        <StyledWhatsappChannelIcon type={ChannelExternalTypesEnum.WHATSAPP} size={ChannelIconSizesEnum.BIG} />
        <StyledInstagramChannelIcon type={ChannelExternalTypesEnum.DIALPAD} size={ChannelIconSizesEnum.BIG} />
        <StyledGmailChannelIcon type={ChannelTypesEnum.GMAIL} size={ChannelIconSizesEnum.BIG} />
        <StyledFacebookChannelIcon type={ChannelTypesEnum.FACEBOOK} size={ChannelIconSizesEnum.BIG} />

        <StyledOnboardingInboxIllustration
          companyName=""
          team={defaultTeam ?? {name: '', color: PaletteColorsEnum.RED}}
          inbox={{name: '', color: theme.greys.shade50}}
          feature={OnboardingInboxVisualFeature.CHANNELS}
        />
      </StyledImportMessagesVisualContainer>
    </StyledVisualContainer>
  );
};

function computeOffset(position: number, scale: number) {
  return position + (scale - 40) / 2; // 40 because that's the icon's original size.
}

function computeFadeInAnimation(animationName: string, delayMs: number, durationMs: number, scale?: number) {
  return `
    animation-name: ${animationName};
    animation-delay: ${delayMs}ms;
    animation-duration: ${durationMs}ms;
    animation-fill-mode: forwards;
    opacity: 0;
    transform: scale(${scale ? `${scale * 0.5}` : '1'});
    @keyframes ${animationName} {
      0% {
        opacity: 0;
        transform: scale(${scale ? `${scale * 0.5}` : '1'});
      }
      100% {
        opacity: 1;
        transform: scale(${scale || '1'});
      }
    }
  `;
}
